import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addDays, subDays, parseISO } from "date-fns";
import { enUS } from "date-fns/locale";
import { AlertTriangle, CalendarPlus, Loader2, Pencil, Search, XCircle, Info, CheckCircle2, ClipboardPlus, ExternalLink, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDirectory, useMeta } from "@/hooks/useMeta";
import { fmtDateTime, textColorFor } from "@/lib/format";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Btn, LabeledSelect, PageHeader, FieldRow } from "@/components/dashboard/Ui";
import { AssignAppointmentDialog } from "@/components/dashboard/AssignAppointmentDialog";

const localizer = dateFnsLocalizer({ format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }), getDay, locales: { "en-US": enUS } });
const DnDCalendar = withDragAndDrop(Calendar);

function SubmissionPicker({ open, onOpenChange, onPick }) {
  const [q, setQ] = useState("");
  const { data: meta } = useMeta();
  const { data, isLoading } = useQuery({
    queryKey: ["submissions", "picker", q],
    queryFn: async () => (await api.get("/submissions", { params: { q: q || undefined, status: "new,contacted,scheduled,no_response", page_size: 30, sort_by: "created_at", sort_dir: "desc" } })).data.items,
    enabled: open,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="submission-picker-dialog">
        <DialogHeader>
          <DialogTitle>Which submission is this appointment for?</DialogTitle>
          <DialogDescription>Pick the client request to schedule in this slot.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input className="emerge-input !h-10 ps-9 text-sm" placeholder="Search by name or phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="submission-picker-search" autoFocus />
        </div>
        <div className="max-h-[50vh] overflow-y-auto divide-y divide-border rounded-[10px] border border-border">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          )}
          {(data || []).map((s) => (
            <button key={s.id} type="button" onClick={() => onPick(s)} className="table-row-hover flex w-full items-center gap-3 px-4 py-3 text-start" data-testid="submission-picker-row">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-foreground">{s.beneficiary_name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {meta?.services?.[s.service] || s.service} - {s.contact_name} - <span dir="ltr">{s.phone}</span>
                </p>
              </div>
              <StatusBadge status={s.status} />
            </button>
          ))}
          {data && data.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">No open submissions match.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SchedulePage() {
  const qc = useQueryClient();
  const { user, isSupervisor } = useAuth();
  const { data: meta } = useMeta();
  const { data: directory } = useDirectory();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [range, setRange] = useState({ start: subDays(startOfMonth(new Date()), 7), end: addDays(endOfMonth(new Date()), 7) });
  const [adminFilter, setAdminFilter] = useState("");
  // Waiting-list view: only appointments that hold a slot but are not confirmed.
  const [waitingOnly, setWaitingOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editAppt, setEditAppt] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [slotStart, setSlotStart] = useState(null);
  const [pickedSubmission, setPickedSubmission] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  // An admin only ever sees themselves, so shade their own hours. A supervisor
  // sees everyone by default, where shading would be meaningless - it appears
  // once they filter down to one therapist.
  const availabilityUserId = isSupervisor ? adminFilter : user?.id;

  const { data: availability } = useQuery({
    queryKey: ["availability", availabilityUserId],
    queryFn: async () => (await api.get(`/availability/${availabilityUserId}`)).data,
    enabled: !!availabilityUserId,
  });

  /** HH:MM blocks that apply on a given Date, exception winning over the pattern. */
  const blocksForDate = useCallback(
    (d) => {
      if (!availability) return null;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const exc = (availability.exceptions || []).find((e) => e.date === key);
      if (exc) return exc.unavailable ? [] : exc.slots || [];
      const WD = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
      return availability.weekly?.[WD[d.getDay()]] || [];
    },
    [availability]
  );

  /** Grey out any slot the selected therapist is not available for. */
  const slotPropGetter = useCallback(
    (d) => {
      const blocks = blocksForDate(d);
      if (blocks === null) return {};
      const mins = d.getHours() * 60 + d.getMinutes();
      const inside = blocks.some((b) => {
        const [sh, sm] = b.start.split(":").map(Number);
        const [eh, em] = b.end.split(":").map(Number);
        return mins >= sh * 60 + sm && mins < eh * 60 + em;
      });
      return inside
        ? {}
        : { style: { backgroundColor: "hsl(210 20% 96%)", backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 6px, hsl(210 16% 92%) 6px, hsl(210 16% 92%) 12px)" } };
    },
    [blocksForDate]
  );

  const colorOf = useMemo(() => {
    const map = {};
    (directory || []).forEach((u) => (map[u.id] = u.color || "#2f5d83"));
    return (id) => map[id] || "#2f5d83";
  }, [directory]);

  const { data, isLoading } = useQuery({
    queryKey: ["appointments", range.start.toISOString(), range.end.toISOString(), adminFilter, waitingOnly],
    queryFn: async () =>
      (
        await api.get("/appointments", {
          params: {
            start: range.start.toISOString(),
            end: range.end.toISOString(),
            admin_id: adminFilter || undefined,
            status: waitingOnly ? "scheduled" : "scheduled,completed",
            confirmed: waitingOnly ? false : undefined,
          },
        })
      ).data,
  });

  const appointments = useMemo(() => data?.items || [], [data]);
  const unconfirmedCount = data?.unconfirmed_count || 0;

  const events = useMemo(
    () =>
      appointments.map((a) => ({
        id: a.id,
        title: `${a.beneficiary_name} - ${meta?.appointment_types?.[a.type] || a.type}`,
        start: parseISO(a.start),
        end: parseISO(a.end),
        resource: a,
      })),
    [appointments, meta]
  );

  // Deep link ?appointment=ID
  const focusId = params.get("appointment");
  useEffect(() => {
    if (!focusId) return;
    api
      .get(`/appointments/${focusId}`)
      .then((res) => {
        const a = res.data.appointment;
        setDate(parseISO(a.start));
        setSelected(a);
        setView(Views.DAY);
      })
      .catch((e) => toast.error(errorMessage(e, "Appointment not found")));
  }, [focusId]);

  const onRangeChange = useCallback((r) => {
    if (Array.isArray(r)) setRange({ start: subDays(r[0], 1), end: addDays(r[r.length - 1], 1) });
    else if (r?.start && r?.end) setRange({ start: subDays(r.start, 1), end: addDays(r.end, 1) });
  }, []);

  const moveMutation = useMutation({
    mutationFn: async ({ appt, start, end }) => {
      const minutes = Math.round((end - start) / 60000);
      return (await api.patch(`/appointments/${appt.id}`, { start: start.toISOString(), duration_minutes: Math.max(15, minutes) })).data;
    },
    onSuccess: () => {
      toast.success("Appointment rescheduled - admin notified");
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["submissions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setPendingMove(null);
    },
    onError: (err, vars) => {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setPendingMove({
          ...vars,
          code: detail?.code || "overlap",
          message: detail?.message,
          conflicts: detail?.conflicts || [],
        });
      } else toast.error(errorMessage(err));
    },
  });

  const handleMove = ({ event, start, end }) => {
    if (!isSupervisor) {
      toast.error("Only supervisors can reschedule by dragging");
      return;
    }
    if (event.resource.status !== "scheduled") return;
    moveMutation.mutate({ appt: event.resource, start, end });
  };

  const cancelMutation = useMutation({
    mutationFn: async (a) => (await api.post(`/appointments/${a.id}/cancel`, { reason: "Cancelled from schedule" })).data,
    onSuccess: () => {
      toast.success("Appointment cancelled");
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["submissions"] });
      setCancelTarget(null);
      setSelected(null);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const confirmMutation = useMutation({
    mutationFn: async ({ appt, confirmed }) =>
      (await api.post(`/appointments/${appt.id}/confirm`, { confirmed })).data,
    onSuccess: (data, vars) => {
      toast.success(vars.confirmed ? "Appointment confirmed" : "Moved back to the waiting list");
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setSelected(data.appointment || null);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const completeMutation = useMutation({
    mutationFn: async (a) => (await api.post(`/appointments/${a.id}/complete`)).data,
    onSuccess: () => {
      toast.success("Marked as completed");
      qc.invalidateQueries({ queryKey: ["appointments"] });
      setSelected(null);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const eventPropGetter = useCallback(
    (event) => {
      const bg = colorOf(event.resource.admin_id);
      const completed = event.resource.status === "completed";
      const unconfirmed = event.resource.confirmed === false;
      return {
        style: {
          backgroundColor: bg,
          color: textColorFor(bg),
          opacity: completed ? 0.55 : 1,
          borderColor: unconfirmed ? "rgba(255,255,255,.9)" : "rgba(255,255,255,.35)",
          // Unconfirmed slots are hatched and dashed so they never read as settled.
          borderStyle: unconfirmed ? "dashed" : "solid",
          borderWidth: unconfirmed ? 2 : 1,
          backgroundImage: unconfirmed
            ? "repeating-linear-gradient(45deg, rgba(255,255,255,.30), rgba(255,255,255,.30) 5px, transparent 5px, transparent 10px)"
            : "none",
        },
      };
    },
    [colorOf]
  );

  const admins = (directory || []).filter((u) => u.status === "active");
  const canManage = (a) => a && (isSupervisor || a.admin_id === user?.id);

  return (
    <div data-testid="schedule-page">
      <PageHeader
        title="Schedule"
        description={isSupervisor ? "All therapists' appointments. Filter to one therapist to see their available hours. Drag an appointment to reschedule it, or click an empty slot to assign one." : "Your appointments and your available hours. Shaded times are outside your availability. Supervisors assign and reschedule appointments."}
        actions={
          isSupervisor && (
            <div className="flex flex-wrap items-center gap-2">
              <Btn
                onClick={() => {
                  setSlotStart(null);
                  setPickerOpen(true);
                }}
                data-testid="schedule-new-appointment-button"
              >
                <CalendarPlus /> New appointment
              </Btn>
              <Btn
                variant={waitingOnly ? "primary" : "secondary"}
                onClick={() => setWaitingOnly((v) => !v)}
                title={waitingOnly ? "Show the full schedule" : "Show only unconfirmed appointments"}
                aria-pressed={waitingOnly}
                data-testid="schedule-waiting-toggle"
              >
                <ListChecks /> Waiting list
                {unconfirmedCount > 0 && (
                  <span className="ms-1 rounded-full bg-[hsl(38_80%_50%)] px-1.5 py-0.5 text-[11px] font-bold text-white">
                    {unconfirmedCount}
                  </span>
                )}
              </Btn>
              <Btn
                variant="secondary"
                onClick={() => window.open("/supervisorintake", "_blank", "noopener")}
                title="Open the supervisor intake form in a new tab"
                data-testid="schedule-new-intake-button"
              >
                <ClipboardPlus /> newintake+
                <ExternalLink className="h-3.5 w-3.5 opacity-70" />
              </Btn>
            </div>
          )
        }
      />

      {isSupervisor && (
        <div className="emerge-card mb-4 flex flex-wrap items-center gap-3 px-4 py-3" data-testid="schedule-admin-filter">
          <LabeledSelect id="admin-filter" value={adminFilter} onChange={(e) => setAdminFilter(e.target.value)} className="min-w-[200px]" aria-label="Filter by admin" data-testid="schedule-admin-filter-select">
            <option value="">All team members</option>
            {admins.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </LabeledSelect>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {admins.map((a) => (
              <button key={a.id} type="button" onClick={() => setAdminFilter(adminFilter === a.id ? "" : a.id)} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${adminFilter === a.id ? "border-primary bg-primary/5" : "border-border bg-white"}`} data-testid="schedule-legend-item">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: a.color }} />
                {a.name}
              </button>
            ))}
          </div>
          {isLoading && <Loader2 className="ms-auto h-4 w-4 animate-spin text-primary" />}
        </div>
      )}

      {waitingOnly && (
        <div
          className="mb-4 flex flex-wrap items-center gap-2 rounded-[10px] border border-[hsl(38_55%_78%)] bg-[hsl(38_90%_95%)] px-4 py-3 text-sm text-[hsl(30_70%_25%)]"
          data-testid="schedule-waiting-banner"
        >
          <ListChecks className="h-4 w-4" />
          <span>
            <strong>Waiting list</strong> - showing only appointments that hold a slot but are not confirmed
            {unconfirmedCount > 0 ? ` (${unconfirmedCount} in this period)` : ""}.
          </span>
          <button
            type="button"
            onClick={() => setWaitingOnly(false)}
            className="ms-auto font-semibold underline"
            data-testid="schedule-waiting-clear"
          >
            Show the full schedule
          </button>
        </div>
      )}

      <div className="emerge-card emerge-rbc p-4" style={{ height: "calc(100dvh - 15rem)", minHeight: 560 }}>
        <DnDCalendar
          localizer={localizer}
          events={events}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          onRangeChange={onRangeChange}
          eventPropGetter={eventPropGetter}
          slotPropGetter={slotPropGetter}
          onSelectEvent={(e) => setSelected(e.resource)}
          selectable={isSupervisor}
          onSelectSlot={(slot) => {
            if (!isSupervisor) return;
            setSlotStart(slot.start);
            setPickerOpen(true);
          }}
          onEventDrop={handleMove}
          onEventResize={handleMove}
          resizable={isSupervisor}
          draggableAccessor={(e) => isSupervisor && e.resource.status === "scheduled"}
          step={30}
          timeslots={2}
          min={new Date(1970, 0, 1, 7, 0)}
          max={new Date(1970, 0, 1, 21, 0)}
          scrollToTime={new Date(1970, 0, 1, 8, 0)}
          popup
          tooltipAccessor={(e) => `${e.title} - ${e.resource.admin_name}`}
        />
      </div>

      {/* Event details */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), focusId && setParams({}, { replace: true }))}>
        <DialogContent className="max-w-md bg-white" data-testid="appointment-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: colorOf(selected.admin_id) }} />
                  <StatusBadge status={selected.status === "scheduled" ? "scheduled" : selected.status === "completed" ? "completed" : "cancelled"} label={selected.status} />
                </div>
                <DialogTitle className="text-xl">{selected.beneficiary_name}</DialogTitle>
                <DialogDescription>{meta?.appointment_types?.[selected.type] || selected.type}</DialogDescription>
              </DialogHeader>
              <dl className="divide-y divide-border">
                <FieldRow label="When">
                  {fmtDateTime(selected.start)} - {selected.duration_minutes} min
                </FieldRow>
                <FieldRow label="Confirmation">
                  {selected.confirmed === false ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(38_80%_50%)]/15 px-2.5 py-1 text-xs font-semibold text-[hsl(30_70%_28%)]" data-testid="appointment-unconfirmed-badge">
                      <ListChecks className="h-3.5 w-3.5" /> Not confirmed - on the waiting list
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(142_55%_42%)]/15 px-2.5 py-1 text-xs font-semibold text-[hsl(142_60%_24%)]">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed
                    </span>
                  )}
                </FieldRow>
                {selected.reminder_status && (
                  <FieldRow label="WhatsApp reminder">
                    <span data-testid="appointment-reminder-status">
                      {selected.reminder_status === "sent"
                        ? `Sent ${selected.reminder_sent_at ? fmtDateTime(selected.reminder_sent_at) : ""}`
                        : selected.reminder_status === "skipped_no_phone"
                          ? "Not sent - no phone number on the submission"
                          : "Queued"}
                    </span>
                  </FieldRow>
                )}
                <FieldRow label="Appointment type">
                  <span className="font-semibold text-foreground">
                    {meta?.appointment_types?.[selected.type] || selected.type}
                  </span>
                </FieldRow>
                <FieldRow label="Assigned to">{selected.admin_name}</FieldRow>
                <FieldRow label="Service">{meta?.services?.[selected.service] || selected.service}</FieldRow>
                {selected.note && <FieldRow label="Note">{selected.note}</FieldRow>}
                <FieldRow label="Created by">{selected.created_by_name}</FieldRow>
              </dl>
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <Link to={`/dashboard/submissions/${selected.submission_id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline" data-testid="appointment-open-submission-link">
                  <Info className="h-4 w-4" /> Open submission
                </Link>
                {selected.status === "scheduled" && canManage(selected) && (
                  <div className="flex gap-2">
                    {canManage(selected) && selected.status === 'scheduled' && (
                      <Btn
                        variant={selected.confirmed === false ? 'primary' : 'secondary'}
                        onClick={() => confirmMutation.mutate({ appt: selected, confirmed: selected.confirmed === false })}
                        disabled={confirmMutation.isPending}
                        data-testid="appointment-confirm-button"
                      >
                        <CheckCircle2 /> {selected.confirmed === false ? 'Confirm' : 'Mark unconfirmed'}
                      </Btn>
                    )}
                    <Btn variant="secondary" size="sm" onClick={() => completeMutation.mutate(selected)} data-testid="appointment-complete-button">
                      <CheckCircle2 /> Completed
                    </Btn>
                    {isSupervisor && (
                      <Btn variant="secondary" size="sm" onClick={() => setEditAppt(selected)} data-testid="appointment-edit-button">
                        <Pencil /> Edit
                      </Btn>
                    )}
                    <Btn variant="dangerOutline" size="sm" onClick={() => setCancelTarget(selected)} data-testid="appointment-cancel-button">
                      <XCircle /> Cancel
                    </Btn>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {editAppt && (
        <AssignAppointmentDialog
          open={!!editAppt}
          onOpenChange={(o) => !o && setEditAppt(null)}
          appointment={editAppt}
          onSaved={() => setSelected(null)}
        />
      )}

      <SubmissionPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={(s) => {
          setPickerOpen(false);
          setPickedSubmission(s);
        }}
      />
      {pickedSubmission && <AssignAppointmentDialog open={!!pickedSubmission} onOpenChange={(o) => !o && setPickedSubmission(null)} submission={pickedSubmission} defaultStart={slotStart} />}

      {/* Move refused: overlap, or outside the therapist's hours */}
      <AlertDialog open={!!pendingMove} onOpenChange={(o) => !o && setPendingMove(null)}>
        <AlertDialogContent className="bg-white" data-testid="schedule-event-double-booking-alert">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {pendingMove?.code === "outside_availability" ? "Outside available hours" : "That time is already taken"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>{pendingMove?.message}</p>
                {pendingMove?.conflicts?.length > 0 && (
                  <ul className="mt-2 list-disc ps-5 text-sm text-foreground">
                    {pendingMove.conflicts.map((c) => (
                      <li key={c.id}>
                        {fmtDateTime(c.start)} - {c.beneficiary_name}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-3 text-sm text-muted-foreground">
                  The appointment has been left where it was.
                  {pendingMove?.code === "outside_availability"
                    ? " Change the therapist's hours on the Availability page if this is a genuine exception."
                    : " Choose a time that is free."}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="double-booking-keep-button">Close</AlertDialogCancel>
            {pendingMove?.code === "outside_availability" && (
              <AlertDialogAction asChild>
                <Link to="/dashboard/availability">Open availability</Link>
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription>The assigned admin will be emailed a cancellation notice (with a calendar update).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={() => cancelMutation.mutate(cancelTarget)} data-testid="schedule-confirm-cancel-button">
              Cancel appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
